import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_MAX_FILE_SIZE_MB,
  isAllowedImageType,
  isWithinImageSizeLimit,
} from 'src/utils/imageUpload';
import styles from './LogoHeadSettings.module.scss';
import Icon from '../Icon/Icon';
import Button, { ButtonToneProps } from '../Button/Button';
import Image from '../Image/Image';

export interface LogoHeadSettingsProps {
  className?: string;
  title: string;
  description: string;
  onSubmit: (file: File) => void;
  onDelete?: () => void;
  logo: string | null;
}

const LogoHeadSettings: React.FC<LogoHeadSettingsProps> = ({
  className = '',
  title,
  description,
  logo = '',
  onSubmit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState<string | null>(logo);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [hasImage, setHasImage] = useState(false);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      event.target.value = '';

      if (!isAllowedImageType(file)) {
        toast.error(t('errors.invalid_image_type'));
        return;
      }
      if (!isWithinImageSizeLimit(file)) {
        toast.error(t('errors.image_too_large', { size: IMAGE_UPLOAD_MAX_FILE_SIZE_MB }));
        return;
      }

      const fileUrl = URL.createObjectURL(file);
      setLogoUrl(fileUrl);
      setLogoFile(file);
    }
  };

  const handleDelete = () => {
    if (logoFile) {
      setLogoFile(null);
      setLogoUrl(logo);
      return;
    }
    onDelete?.();
  };

  useEffect(() => {
    setLogoUrl(logo);
  }, [logo]);

  return (
    <div className={classNames(styles.root, className)}>
      <div className={classNames('relative', styles.logo)}>
        <Image
          imageUrl={logoUrl}
          alt="Profile"
          fallbackComponent={<Icon name="myProfile" width={64} height={64} />}
          onResolved={setHasImage}
        />
        <input type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={handleLogoChange} />
      </div>
      <div className="flex flex-col gap-y-8">
        <h2 className="typo-title-24 text-full">{title}</h2>
        <span className="typo-text-12 text-faded">{description}</span>
      </div>
      <div className="flex items-center gap-x-12 flex-shrink-0 lg:ml-auto">
        <Button
          disabled={!logoFile}
          onClick={() => logoFile && onSubmit(logoFile)}
          title={t('settings.upload_new_picture')}
          tone={ButtonToneProps.PRIMARY_BLUE}
        />
        {onDelete && (
          <Button
            disabled={!hasImage && !logoFile}
            onClick={handleDelete}
            title={t('general.delete')}
            tone={ButtonToneProps.ERROR}
          />
        )}
      </div>
    </div>
  );
};

export default LogoHeadSettings;
